// Cost Plus Forecast — the VoltaGrid jobs' forecast tab, laid out exactly
// like the Bid Forecast (same columns, rows and cards) so the two read the
// same way, plus a group of three billable columns on the right: Billable
// Budget (the Day Zero's Billable Cost), Billable EAC (billable to date off
// the Billing tab plus the cost to go priced at a bill-to-cost ratio) and
// Delta from Billable Budget (Billable EAC less Billable Budget). The
// Contract Amount row becomes Revenue (Billable), so Job Profit and Gross
// Margin work on a cost-plus job. The model is helpers/costPlusForecast;
// the inputs are the Overview's plan (helpers/jobBudgetEac) and the Billing
// tab's priced lines (billingRevenue). Nothing is priced here.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { View, Text, Pressable, TextInput, Icon3 } from '../lib/react-native-compat';
import { fNum, pNum, updateUserDataArray } from '../db/company';
import {
    selectedJobAtom, selectedVersionAtom, jobUserDataAtom, jobOpenCommitmentsAtom, jobCostDetailAtom, jobBillingsAtom,
    selectedWIPAtom, dataSourceVersionAtom, projectCompleteAtom, selectedVersionLockedAtom, userIdAtom, userNameAtom, selectedJobDataAtom, overheadDataAtom,
} from '../store/company';
import useBillingRevenue from './billingRevenue';
import { jobPlanByCode } from '../helpers/jobBudgetEac';
import { versionStartFor } from '../helpers/fieldBonus';
import { isVoltaGridJob } from '../helpers/voltaGrid';
import { costPlusModel, forecastFieldOf, ratioText, sentTestOf, howText, invoiceGapsOf } from '../helpers/costPlusForecast';
import { COST_TYPE_DESCRIPTION } from '../helpers/procurementCategories';
import { ruleRowOf } from './billingRates';

const CT_DESC = {
    '51100': 'Regular Time', '51200': 'Overtime', '51300': 'Burden', '51350': 'Burden - Fringe', '51400': 'BYOD', '51500': 'Per Diem',
    '51600': 'Truck Pay', '51700': 'Welding Rig Pay', '51800': 'Field Bonus', '52100': 'Inside Equipment Rental',
    ...COST_TYPE_DESCRIPTION,
};

// Why a not-billable line is off the invoice, as a Non-Billable group — the
// contract rule that took it off (read as Rates & Rules' Billability Rules
// row, so merged rules group together), else the reason Billing gives.
const nbGroupOf = (t) => {
    // Billable on its own, closed out not billable on an invoice (per diem
    // days the signed tickets didn't carry): the reason the invoice froze.
    if (t.nbByClose) return t.nbReason || 'Closed not billable on an invoice';
    if (t.ccNb) return 'Non-billable cost code';
    if (t.cr && t.cr.effect === 'nb') return ruleRowOf(t.cr).label;
    if (!t.autoNb) return 'Marked not billable on Transactions';
    const why = String(t.nbReason || '');
    if (why.startsWith('Rental — bills on')) return 'Rental — bills on HeavyJob hours';
    return why || 'Not billable';
};

// The Bid Forecast's number format: whole dollars, parentheses for a
// negative, blank for nothing.
const formN = (x) => {
    if (x === null || x === undefined || !Number.isFinite(x)) return '';
    const r = Math.round(x);
    if (!r) return '';
    return r > 0 ? fNum(r, 1) : `(${fNum(-r, 1)})`;
};
const pct2 = (p) => (p === null || p === undefined || !Number.isFinite(p) ? '' : `${(p * 100).toFixed(2)}%`);
const isNeg = (s) => typeof s === 'string' && s.length > 1 && (s[0] === '(' || s[0] === '-');
// JTD Actual Cost split three ways — the three add up to JTD: cost that has
// gone to the customer on an invoice, cost still to bill (or on a summary
// that hasn't been sent), and the Non-Billable row's cost that never bills.
// Burden and payroll taxes follow the payroll week they were paid with.
const SENT_NOTE = 'Cost to date on lines sent to the customer on an invoice (Uploaded to FTMS or Invoiced), plus the burden, payroll taxes and truck / rig pay of the payroll weeks those invoices billed';
const UNSENT_NOTE = 'Cost to date not on a sent invoice: lines still to bill, lines on a Draft or Kicked Back summary, and the burden, payroll taxes and truck / rig pay of the payroll weeks not invoiced yet';
const NB_COL_NOTE = 'Cost to date on lines the Billing tab has marked not billable — the Non-Billable row';
const TO_BILL_NOTE = 'Not invoiced: the billable amount on every other line to date at its Billing tab price (lines still to bill, lines on a Draft or Kicked Back summary), with the ST / OT lines the rate sheet can\'t price yet estimated at the job\'s own rate';
const BILLED_NOTE ='Billed: the billable amount on the lines sent to the customer on an invoice (Uploaded to FTMS or Invoiced), plus what those invoices billed with no JDE line behind it — what the cost in this column billed';
// The three start closed; the caret on JTD Actual Cost opens them.
const SENT_TOGGLE = 'Show or hide JTD Cost Invoiced, Not Invoiced and Non-Billable';

// The Bid Forecast's grid styles (bidForecast.js `s`), at its 11px row size.
// Every column is a fixed box that never shrinks: a row wider than the page
// scrolls, it doesn't squeeze — a header that gave up 4px a column while the
// rows kept theirs is how the two drift apart.
const FIXED = { flexShrink: 0, flexGrow: 0, boxSizing: 'border-box' };
const COL = 84;
const CELL = { ...FIXED, width: COL, minWidth: COL, alignItems: 'center', fontSize: 11, padding: 2, textAlign: 'right', borderLeftWidth: 1, borderColor: '#e0e0e0', borderWidth: 1, border: '1px solid #e0e0e0' };
const LABEL = { ...FIXED, width: 180, minWidth: 180, alignItems: 'center', fontSize: 11, fontWeight: 500, padding: 2, paddingLeft: 10, borderLeftWidth: 1, borderColor: '#F9F8FB', backgroundColor: '#e0e0e0', borderBottomWidth: 1 };
const HEAD = { ...FIXED, width: COL, minWidth: COL, alignItems: 'center', fontSize: 10, padding: 5, textAlign: 'right', backgroundColor: '#e0e0e0', borderRightWidth: 1, borderColor: 'white', fontWeight: 500, color: '#161616' };
const SUB = { ...CELL, backgroundColor: '#f2f2f2', fontSize: 10 };
// The spacers between column groups, the caret slot, the cost type slot and
// the label column's share of the header.
const GAP = { ...FIXED, width: 50, minWidth: 50 };
const CARET = { ...FIXED, width: 30, minWidth: 30 };
const CT_SLOT = { ...FIXED, width: 40, minWidth: 40 };
const HEAD_LEAD = { ...FIXED, width: 250, minWidth: 250 };
const ROW = { flexDirection: 'row', flexWrap: 'nowrap', paddingBottom: 0 };
const GREEN = { backgroundColor: '#c6efce' };
const RED = { backgroundColor: '#ffc7ce' };
// The Billings card's NTE rows (NTE Amount, Headroom, Run Rate, NTE Reached at
// Run Rate) are hidden for now; flip this to bring them back.
const SHOW_NTE = false;

function CostPlusForecast() {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobBillings] = useAtom(jobBillingsAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [userId] = useAtom(userIdAtom);
    const [userName] = useAtom(userNameAtom);
    const [selectedJobData] = useAtom(selectedJobDataAtom);
    const [overheadData] = useAtom(overheadDataAtom);
    // The job's overhead %, as the Bid Forecast reads it: by the BU's category code.
    const ohPercent = selectedJobData?.Cat_Code_BU_27 ? (overheadData?.[selectedJobData.Cat_Code_BU_27] || 0) : 0;

    const isVg = isVoltaGridJob(selectedJob);
    const billRev = useBillingRevenue({ enabled: isVg });

    // The Bid Forecast's own fields on the version — NTE Amount, physical %
    // complete, notes — read from and saved to the same rows, so the two
    // tabs never disagree. Saves debounce per field, as the Bid Forecast's do.
    const [fields, setFields] = useState({ nteAmount: '', physicalComplete: '', notes: '' });
    const timers = useRef({});
    useEffect(() => {
        const nte = pNum(forecastFieldOf(jobUserData, selectedVersion, selectedJob, 'nteAmount') || 0);
        const phys = forecastFieldOf(jobUserData, selectedVersion, selectedJob, 'physicalComplete');
        setFields({
            nteAmount: nte ? fNum(nte, 1) : '',
            physicalComplete: phys && pNum(phys) < 1 ? String(pNum(phys) * 100) : (phys || ''),
            notes: forecastFieldOf(jobUserData, selectedVersion, selectedJob, 'notes') || '',
        });
    }, [jobUserData, selectedVersion, selectedJob]);
    const saveField = (key, value) => {
        setFields((f) => ({ ...f, [key]: value }));
        if (timers.current[key]) clearTimeout(timers.current[key]);
        timers.current[key] = setTimeout(() => {
            updateUserDataArray({
                userId: userId || '', userName: userName || '', fieldName: key, tab: 'Forecast', rowId: 1,
                version: selectedVersion, value: String(key === 'nteAmount' ? pNum(value) : value), id: selectedJob, lockedVersion,
            }).catch(() => { /* the field keeps the typed value; the next load shows what saved */ });
        }, 2000);
    };
    useEffect(() => () => { Object.values(timers.current).forEach((t) => clearTimeout(t)); }, []);

    // Cost to go per code per class, the Overview's rules.
    const versionStart = versionStartFor(versions, selectedVersion, selectedWIP);
    const plan = useMemo(() => {
        if (!selectedVersion || !Array.isArray(jobUserData) || !Array.isArray(jobOpenCommitments)) return null;
        const mine = jobUserData.filter((d) => String(d?.BU_ID || '').trim() === String(selectedJob || '').trim());
        if (!mine.length) return null;
        return jobPlanByCode({ userData: mine, version: selectedVersion, openCommitments: jobOpenCommitments, versionStart, projectComplete });
    }, [jobUserData, jobOpenCommitments, selectedVersion, versionStart, projectComplete, selectedJob]);

    const jdeBilled = useMemo(() => (Array.isArray(jobBillings) ? jobBillings.reduce((a, b) => a + pNum(b?.Invoice_Amount || 0), 0) : 0), [jobBillings]);

    const model = useMemo(() => costPlusModel({
        userData: jobUserData, version: selectedVersion, job: selectedJob, plan, openCommitments: jobOpenCommitments, jobCostDetail,
        txns: billRev.txns, fuelCtx: billRev.fuelCtx, invoices: billRev.invoices, priceCtx: billRev.priceCtx, rateCfg: billRev.rateCfg, ready: !!billRev.ready, ohPercent, versionStart,
        nte: pNum(fields.nteAmount), jdeBilled,
    }), [jobUserData, selectedVersion, selectedJob, plan, jobOpenCommitments, jobCostDetail, billRev, ohPercent, versionStart, fields.nteAmount, jdeBilled]);

    const [open, setOpen] = useState({});
    const [showSent, setShowSent] = useState(false);

    // Non-Billable's groups, biggest first, each with its cost types. `sent`
    // is the part a sent summary closed out at $0 (the invoiced column).
    const nbGroups = useMemo(() => {
        if (!model.ready) return [];
        const isSent = sentTestOf(billRev.invoices);
        const by = {};
        (billRev.txns || []).forEach((t) => {
            // A rental voucher billing on HeavyJob hours isn't not-billable
            // cost — the model keeps it in Equipment.
            if (!t || !t.nb || t.ruUnit) return;
            const label = nbGroupOf(t);
            const g = by[label] || (by[label] = { label, cost: 0, sent: 0, byCt: {}, sentCt: {} });
            const c = pNum(t.cost);
            const ct = String(t.ct || '');
            g.cost += c;
            g.byCt[ct] = (g.byCt[ct] || 0) + c;
            if (isSent(t.nbInv)) {
                g.sent += c;
                g.sentCt[ct] = (g.sentCt[ct] || 0) + c;
            }
        });
        return Object.values(by)
            .map((g) => ({ ...g, cts: Object.keys(g.byCt).sort().map((ct) => ({ ct, cost: g.byCt[ct], sent: g.sentCt[ct] || 0 })) }))
            .sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost));
    }, [model.ready, billRev.txns, billRev.invoices]);

    if (!isVg) {
        return <View style={{ padding: 24 }}><Text style={{ color: '#64748b', fontSize: 13 }}>Cost Plus Forecast is only on the VoltaGrid jobs.</Text></View>;
    }

    const { total, revenue, profit, oh, gm, nte, billing, pct } = model;

    // A grid row in the Bid Forecast's shape: caret | label | spacer | K2 |
    // Bid | CO | Current | box | JTD (| invoiced | not invoiced |
    // non-billable, when open) | Committed | box | ETC | EAC | box | deltas |
    // box | Billable Budget | Billable EAC | Delta from Billable Budget.
    // `bill` is the hover note on the budget and EAC billable cells — the
    // ratio the class bills at.
    const sentN = (v) => (model.ready ? formN(v) : '');
    const cell = (v, grey, extra, title) => (
        <Text style={[CELL, { backgroundColor: grey ? '#efefef' : '#fff' }, extra]} title={title}>{v}</Text>
    );
    const gridRow = ({ key, label, grey, caret, onCaret, sub, k2, bid, co, current, billBudget, jtd, jtdSent, jtdUnsent, jtdNb, committed, etc, eac, billEac, deltaEac, deltaBid, deltaBill, deltaWip, bill, jtdNote, sentNote, unsentNote, nbNote }) => (
        <View key={key} style={ROW}>
            {caret ? (
                <Pressable onPress={onCaret} style={{ ...CARET, alignItems: 'center', justifyContent: 'center' }}>
                    <Icon3 name={caret === 'open' ? 'MdKeyboardArrowDown' : 'MdArrowForwardIos'} size={16} color="#9ca3af" />
                </Pressable>
            ) : <View style={CARET} />}
            <Text style={[LABEL, sub ? { backgroundColor: '#f2f2f2', fontSize: 10, fontWeight: 500, color: '#161616' } : null]}>{label}</Text>
            <Text style={sub ? [CELL, CT_SLOT, { backgroundColor: '#f2f2f2', fontSize: 9 }] : CT_SLOT}>{sub ? sub.ct : ''}</Text>
            {cell(k2, grey, sub ? SUB : null)}
            {cell(bid, grey, sub ? SUB : null)}
            {cell(co, grey, sub ? SUB : null)}
            {cell(current, grey, sub ? SUB : null)}
            <View style={GAP} />
            {cell(jtd, grey, sub ? SUB : null, jtdNote)}
            {showSent && cell(jtdSent, grey, sub ? SUB : null, jtdSent ? (sentNote || SENT_NOTE) : undefined)}
            {showSent && cell(jtdUnsent, grey, sub ? SUB : null, jtdUnsent ? (unsentNote || UNSENT_NOTE) : undefined)}
            {showSent && cell(jtdNb, grey, sub ? SUB : null, jtdNb ? (nbNote || NB_COL_NOTE) : undefined)}
            {cell(committed, grey, sub ? SUB : null)}
            <View style={GAP} />
            {cell(etc, grey, sub ? SUB : null)}
            {cell(eac, grey, sub ? SUB : null)}
            <View style={GAP} />
            <Text style={[CELL, { borderLeftWidth: 1 }, sub ? { ...SUB, ...(deltaEac ? (isNeg(deltaEac) ? RED : GREEN) : {}) } : null]}>{deltaEac}</Text>
            <Text style={[CELL, sub ? { ...SUB, ...(deltaBid ? (isNeg(deltaBid) ? RED : GREEN) : {}) } : null]}>{deltaBid}</Text>
            <Text style={[CELL, { borderLeftWidth: 1 }, sub ? SUB : null]}>{deltaWip}</Text>
            <View style={GAP} />
            {cell(billBudget, grey, sub ? SUB : null, bill)}
            {cell(billEac, grey, sub ? SUB : null, bill)}
            {cell(deltaBill, grey, sub ? SUB : null)}
        </View>
    );

    // How a row's Billable EAC is built, for the hover on its billable cells:
    // invoiced + not invoiced (priced) + committed and ETC (priced), and how
    // the cost to go bills.
    const n0 = (v) => formN(v) || '0';
    const billNote = (c) => {
        if (!model.ready) return 'Billing lines not priced yet';
        const how = howText(c.toGoHow);
        return `Billable EAC = invoiced ${n0(c.sentBill)} + not invoiced ${n0(c.unsentBill)}`
            + `${c.noRateEst ? ` (${n0(c.noRateEst)} of it estimated for lines with no rate yet)` : ''}`
            + ` + committed ${n0(c.billCommitted)} + ETC ${n0(c.billEtc)}`
            + `${how ? `. Cost to go: ${how}` : ''}`;
    };

    // The rows a class renders: its cost line and, when open, its cost types
    // and whatever of its ETC the forecast carries with no cost type.
    const classRows = (c) => {
        const isOpen = !!open[c.key];
        // A cost type whose cost was all not billable, with no budget, PO or
        // ETC, is only on the Non-Billable row.
        const subs = [...c.subRows.filter((s) => s.costBudget || s.coCost || s.jtdCost || s.committed || s.etc || s.jtdBill), ...(c.noCt ? [c.noCt] : [])];
        const rows = [
            gridRow({
                key: c.key, label: c.label, caret: subs.length ? (isOpen ? 'open' : 'closed') : null,
                onCaret: () => setOpen((p) => ({ ...p, [c.key]: !p[c.key] })),
                k2: '', bid: formN(c.costBudget), co: formN(c.coCost), current: formN(c.curCostBudget), billBudget: formN(c.curBillBudget),
                jtd: formN(c.jtdCost), jtdSent: sentN(c.sentCost), jtdUnsent: sentN(c.unsentCost), committed: formN(c.committed), etc: formN(c.etc), eac: formN(c.costEac), billEac: formN(c.billEac),
                deltaEac: formN(c.costDeltaEac), deltaBid: formN(c.costDeltaBid), deltaBill: formN(c.billDeltaEac), deltaWip: '', bill: billNote(c),
            }),
        ];
        if (isOpen) {
            subs.forEach((s) => rows.push(gridRow({
                key: `${c.key}-${s.ct || 'noct'}`, label: s.ct ? CT_DESC[s.ct] || s.ct : s.label, sub: s,
                k2: '', bid: formN(s.costBudget), co: formN(s.coCost), current: formN(s.curCostBudget), billBudget: formN(s.curBillBudget),
                jtd: formN(s.jtdCost), jtdSent: sentN(s.sentCost), jtdUnsent: sentN(s.unsentCost), committed: formN(s.committed), etc: formN(s.etc), eac: formN(s.costEac), billEac: formN(s.billEac),
                deltaEac: formN(s.costDeltaEac), deltaBid: formN(s.costDeltaBid), deltaBill: formN(s.billDeltaEac), deltaWip: '', bill: billNote(s),
            })));
        }
        return rows;
    };

    // Non-Billable: the cost to date on lines the Billing tab has marked not
    // billable, by why when open (nbGroups), then by cost type. The model
    // takes it out of the classes, so it foots into Total Costs. All of it
    // is the JTD Cost Non-Billable column; the part a sent invoice closed
    // out at $0 is in that cell's hover.
    const NB_NOTE = 'Cost to date on lines the Billing tab has marked not billable, taken out of the classes above (burden is never a Billing line, so it stays in Labor)';
    const nbRow = model.nonBillable;
    const closedNote = (closed) => `${NB_COL_NOTE}${Math.round(closed) ? ` — ${formN(closed)} of it closed out at $0 on sent invoices` : ''}`;
    const nonBillableRows = () => {
        const isOpen = !!open.nonBillable;
        const subs = nbGroups;
        const empty = { k2: '', bid: '', co: '', current: '', billBudget: '', committed: '', etc: '', eac: '', billEac: '', deltaEac: '', deltaBid: '', deltaBill: '', deltaWip: '' };
        const rows = [
            gridRow({
                ...empty, key: 'nonBillable', label: <>Non-Billable<span style={{ fontSize: 9, fontWeight: 400, marginLeft: 4, whiteSpace: 'nowrap' }}>(not including burden)</span></>, caret: subs.length ? (isOpen ? 'open' : 'closed') : null,
                onCaret: () => setOpen((p) => ({ ...p, nonBillable: !p.nonBillable })),
                current: model.ready ? formN(nbRow.curCostBudget) : '',
                jtd: model.ready ? formN(nbRow.jtdCost) : '', jtdNb: sentN(nbRow.jtdNb), jtdNote: NB_NOTE, nbNote: closedNote(nbRow.closedCost),
                eac: model.ready ? formN(nbRow.costEac) : '', deltaEac: model.ready ? formN(nbRow.costDeltaEac) : '', deltaBid: model.ready ? formN(nbRow.costDeltaBid) : '',
            }),
        ];
        if (isOpen) {
            subs.forEach((g) => {
                const k = `nonBillable|${g.label}`;
                const gOpen = !!open[k];
                rows.push(gridRow({
                    ...empty, key: k, label: g.label, sub: { ct: g.cts.length === 1 ? g.cts[0].ct : '' }, jtd: formN(g.cost), jtdNb: formN(g.cost), nbNote: closedNote(g.sent),
                    caret: gOpen ? 'open' : 'closed', onCaret: () => setOpen((p) => ({ ...p, [k]: !p[k] })),
                }));
                if (gOpen) {
                    g.cts.forEach((s) => rows.push(gridRow({
                        ...empty, key: `${k}|${s.ct}`, label: <span style={{ paddingLeft: 14 }}>{CT_DESC[s.ct] || s.ct}</span>, sub: s, jtd: formN(s.cost), jtdNb: formN(s.cost), nbNote: closedNote(s.sent),
                    })));
                }
            });
        }
        return rows;
    };

    const status = [];
    if (!model.hasRevBudget) status.push('No Billable Cost column on this version\'s Day Zero — Billable Budget is blank.');
    if (billRev.error) status.push(`Billing lines didn't load: ${billRev.error}`);
    else if (!billRev.ready) status.push('Pricing the billing lines…');
    if (!model.planReady) status.push('Cost to go loads with the forecast tabs.');
    if (billing.noRate) status.push(`${billing.noRate} labor line${billing.noRate === 1 ? '' : 's'} with no rate bill $0 on Billing until the class is mapped on Rates & Rules — the Billable EAC estimates ${billing.noRate === 1 ? 'it' : 'them'} at the job's own ST / OT rate.`);
    // Invoiced revenue is the Billing tab's sent summaries — say where JDE
    // has billed something else (a reissue, an invoice never recorded).
    const gaps = model.ready ? invoiceGapsOf(billRev.invoices, jobBillings) : null;
    if (gaps && (gaps.missing.length || gaps.differ.length)) {
        const parts = [];
        if (gaps.missing.length) parts.push(`${gaps.missing.map((g) => `${g.no} (${formN(g.jde)})`).join(' and ')} ${gaps.missing.length === 1 ? 'is' : 'are'} billed in JDE with no sent Billing summary`);
        gaps.differ.forEach((d) => parts.push(`${d.no} is ${formN(d.billing) || '0'} on its Billing summary and ${formN(d.jde) || '0'} in JDE`));
        status.push(`Invoiced revenue (${formN(revenue.sentBill) || '0'}) comes from the Billing tab's sent summaries, and JDE has billed ${formN(jdeBilled) || '0'}: ${parts.join('; ')}.`);
    }

    const cardRow = (label, value, right) => (
        <View key={label} style={{ flexDirection: 'row' }}>
            <Text style={{ flex: 1, textAlign: 'left', fontSize: 14 }}>{label}</Text>
            {right || <Text style={{ flex: 1, textAlign: 'right', fontSize: 14 }}>{value}</Text>}
        </View>
    );
    const cardInput = (key, width) => (
        <View style={{ width, flexDirection: 'row', position: 'relative' }}>
            <View style={{ width: 3, height: 20, backgroundColor: 'grey', left: 5, top: 0, position: 'absolute' }} />
            <TextInput aria-label={key === 'nteAmount' ? 'NTE amount' : 'Physical complete'} tabIndex="5" style={{ textAlign: 'right', fontSize: 14, width: width - (key === 'physicalComplete' ? 15 : 0), height: 20, padding: 0, borderWidth: 0 }}
                value={fields[key]} editable={!lockedVersion}
                onChangeText={(a) => saveField(key, a)}
                onBlur={key === 'nteAmount' ? () => setFields((f) => ({ ...f, nteAmount: pNum(f.nteAmount) ? fNum(pNum(f.nteAmount), 1) : '' })) : undefined} />
            {key === 'physicalComplete' ? <Text style={{ fontSize: 14, width: 15 }}>%</Text> : null}
        </View>
    );
    const card = { flex: 1, margin: 20, marginBottom: 20, backgroundColor: 'white', borderRadius: 5, padding: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 };

    // The Billable EAC card: the grid's Billable EAC column taken apart by
    // class — invoiced, not invoiced (priced), committed and ETC (priced) —
    // and, opened, how each cost type's cost to go bills.
    const BE_COLS = [['sentBill', 'Invoiced'], ['unsentBill', 'Not invoiced'], ['billCommitted', 'Committed'], ['billEtc', 'ETC'], ['billEac', 'Billable EAC']];
    const BE_NUM = { width: 92, textAlign: 'right', fontSize: 13 };
    const beRow = (key, label, r, { strong, rule, grey } = {}) => (
        <View key={key} style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: 3, borderTopWidth: rule ? 1 : 0, borderColor: '#e0e0e0', borderTop: rule ? '1px solid #e0e0e0' : undefined }}>
            <Text style={{ flex: 1, fontSize: 13, fontWeight: strong ? '600' : '400', color: grey ? '#64748b' : '#161616' }}>{label}</Text>
            {BE_COLS.map(([k]) => (
                <Text key={k} style={{ ...BE_NUM, fontWeight: strong || k === 'billEac' ? '600' : '400', color: grey ? '#64748b' : '#161616' }}>{formN(r[k])}</Text>
            ))}
        </View>
    );
    // Every cost type with cost to go, biggest first, for the "how" list.
    const toGoList = model.ready ? [...model.classes, model.contingency].flatMap((c) => (c.key === 'contingency' ? [c] : [...c.subRows, ...(c.noCt ? [c.noCt] : [])])
        .filter((s) => Math.round(s.committed + s.etc))
        .map((s) => ({ key: `${c.key}|${s.ct || s.key}`, label: c.key === 'contingency' ? 'Contingency' : s.ct ? CT_DESC[s.ct] || s.ct : `${c.label} — ${s.label}`, ct: s.ct || '', cost: s.committed + s.etc, bill: s.billToGo, how: howText(s.toGoHow) })))
        .sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost)) : [];
    const billableCard = model.ready ? (
        <View style={{ ...card, flex: undefined, width: 680, maxWidth: '100%', alignSelf: 'flex-start', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Billable EAC</Text>
            <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                What has been invoiced + every other billable line to date at its Billing tab price + the cost to go priced the way it will bill: open PO lines and ETC through the contract rules and markups, straight time and overtime at the rate sheet on the wages paid so far, fleet and rentals at what they have billed per dollar so far
            </Text>
            <View style={{ flexDirection: 'row', paddingBottom: 3 }}>
                <Text style={{ flex: 1 }} />
                {BE_COLS.map(([k, h]) => <Text key={k} style={{ ...BE_NUM, fontSize: 11, color: '#64748b' }}>{h}</Text>)}
            </View>
            {model.classes.map((c) => beRow(c.key, c.label, c))}
            {Math.round(model.contingency.billEac) ? beRow('contingency', 'Contingency', model.contingency) : null}
            {Math.round(revenue.noLineSent) ? beRow('noline', 'Invoiced with no JDE line', { sentBill: revenue.noLineSent, billEac: revenue.noLineSent }, { grey: true }) : null}
            {beRow('total', revenue.contract != null ? 'Revenue (Billable), priced' : 'Revenue (Billable)', { sentBill: revenue.sentBill, unsentBill: revenue.unsentBill, billCommitted: revenue.committed, billEtc: revenue.pricedEtc, billEac: revenue.pricedEac }, { strong: revenue.contract == null, rule: true })}
            {revenue.contract != null ? beRow('toContract', revenue.tiedTo === 'budget' ? 'To the budget' : 'To the contract', { billEtc: revenue.etc - revenue.pricedEtc, billEac: revenue.eac - revenue.pricedEac }, { grey: true }) : null}
            {revenue.contract != null ? beRow('contract', revenue.tiedTo === 'budget' ? 'Revenue EAC (budget)' : 'Revenue EAC (contract)', { sentBill: revenue.sentBill, unsentBill: revenue.unsentBill, billCommitted: revenue.committed, billEtc: revenue.etc, billEac: revenue.eac }, { strong: true, rule: true }) : null}
            {toGoList.length > 0 && (
                <View style={{ marginTop: 12 }}>
                    <Pressable onPress={() => setOpen((p) => ({ ...p, billableCard: !p.billableCard }))} style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer', alignSelf: 'flex-start' }}>
                        <Icon3 name={open.billableCard ? 'MdKeyboardArrowDown' : 'MdArrowForwardIos'} size={open.billableCard ? 16 : 11} color="#2563eb" />
                        <Text style={{ fontSize: 12, color: '#2563eb', marginLeft: 4 }}>{`How the cost to go is priced (${toGoList.length})`}</Text>
                    </Pressable>
                    {open.billableCard && (
                        <View style={{ marginTop: 6 }}>
                            <View style={{ flexDirection: 'row', paddingVertical: 2, borderBottomWidth: 1, borderColor: '#f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                                <Text style={{ width: 170, fontSize: 11, color: '#64748b' }}>Cost type</Text>
                                <Text style={{ width: 86, fontSize: 11, color: '#64748b', textAlign: 'right' }}>Cost to go</Text>
                                <Text style={{ width: 86, fontSize: 11, color: '#64748b', textAlign: 'right' }}>Billable</Text>
                                <Text style={{ width: 44, fontSize: 11, color: '#64748b', textAlign: 'right' }}>×</Text>
                                <Text style={{ flex: 1, fontSize: 11, color: '#64748b', paddingLeft: 12 }}>How it bills</Text>
                            </View>
                            {toGoList.map((r) => (
                                <View key={r.key} style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: 2, borderBottomWidth: 1, borderColor: '#f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                                    <Text style={{ width: 170, fontSize: 12 }}>{r.label}{r.ct ? <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>{r.ct}</span> : null}</Text>
                                    <Text style={{ width: 86, fontSize: 12, textAlign: 'right' }}>{formN(r.cost) || '0'}</Text>
                                    <Text style={{ width: 86, fontSize: 12, textAlign: 'right' }}>{formN(r.bill) || '0'}</Text>
                                    <Text style={{ width: 44, fontSize: 11, textAlign: 'right', color: '#64748b' }}>{r.cost ? ratioText(r.bill / r.cost) : ''}</Text>
                                    <Text style={{ flex: 1, fontSize: 11, color: '#64748b', paddingLeft: 12 }}>{r.how}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            )}
        </View>
    ) : null;

    // The Labor Billable EAC card: the class's billable EAC taken apart —
    // hours × the rate sheet rates to date, the at-cost lines, the cost to go
    // by cost type — so the grid's number reads as hours × rates.
    const lb = model.laborBill;
    const hrsN = (h) => (h ? fNum(h, 1) : '');
    const rateN = (r) => (r == null || !Number.isFinite(r) ? '' : fNum(r));
    const LB_NUM = { width: 84, textAlign: 'right', fontSize: 13 };
    const lbRow = ({ key, label, how, hrs, rate, bill, strong, rule, grey }) => (
        <View key={key} style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: 3, borderTopWidth: rule ? 1 : 0, borderColor: '#e0e0e0', borderTop: rule ? '1px solid #e0e0e0' : undefined }}>
            <Text style={{ flex: 1, fontSize: 13, fontWeight: strong ? '600' : '400', color: grey ? '#64748b' : '#161616' }}>
                {label}{how ? <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6, fontWeight: 400 }}>{how}</span> : null}
            </Text>
            <Text style={{ ...LB_NUM, color: grey ? '#64748b' : '#161616' }}>{hrs}</Text>
            <Text style={{ ...LB_NUM, color: grey ? '#64748b' : '#161616' }}>{rate}</Text>
            <Text style={{ ...LB_NUM, width: 100, fontWeight: strong ? '600' : '400', color: grey ? '#64748b' : '#161616' }}>{bill}</Text>
        </View>
    );
    const LB_TH = { fontSize: 11, color: '#64748b', textAlign: 'right' };
    const lbClassRow = (c, head) => {
        const num = (w) => (head ? { ...LB_TH, width: w } : { width: w, textAlign: 'right', fontSize: 12 });
        return (
            <View key={head ? 'head' : c.name} style={{ flexDirection: 'row', paddingVertical: 2, borderBottomWidth: 1, borderColor: '#f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                <Text style={{ flex: 1, fontSize: head ? 11 : 12, color: head ? '#64748b' : '#161616' }}>{head ? 'Rate sheet classification' : c.name}</Text>
                <Text style={num(62)}>{head ? 'ST hours' : hrsN(c.stHrs)}</Text>
                <Text style={num(62)}>{head ? 'ST rate' : rateN(c.stRate)}</Text>
                <Text style={num(62)}>{head ? 'OT hours' : hrsN(c.otHrs)}</Text>
                <Text style={num(62)}>{head ? 'OT rate' : rateN(c.otRate)}</Text>
                <Text style={num(86)}>{head ? 'At rate sheet' : formN(c.sheet)}</Text>
                <Text style={num(86)}>{head ? 'Billed' : formN(c.bill)}</Text>
            </View>
        );
    };
    const laborCard = lb ? (
        <View style={{ ...card, flex: undefined, width: 680, maxWidth: '100%', alignSelf: 'flex-start', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Labor Billable EAC</Text>
            <Text style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                Billable to date (JDE hours × the rate sheet rate of the class each person bills as, per diem at cost) + the cost to go (Committed + ETC), priced cost type by cost type
            </Text>
            <View style={{ flexDirection: 'row', paddingBottom: 3 }}>
                <Text style={{ flex: 1 }} />
                <Text style={{ ...LB_NUM, ...LB_TH }}>Hours</Text>
                <Text style={{ ...LB_NUM, ...LB_TH }}>$ / hr</Text>
                <Text style={{ ...LB_NUM, ...LB_TH, width: 100 }}>Billable</Text>
            </View>
            {lbRow({ key: 'st', label: 'Straight time', how: 'hours × rate sheet ST rate', hrs: hrsN(lb.st.hrs), rate: rateN(lb.st.rate), bill: formN(lb.st.sheet) })}
            {lbRow({ key: 'ot', label: 'Overtime', how: 'hours × rate sheet OT rate', hrs: hrsN(lb.ot.hrs), rate: rateN(lb.ot.rate), bill: formN(lb.ot.sheet) })}
            {lbRow({ key: 'rated', label: 'ST + OT composite', how: 'at the rate sheet', hrs: hrsN(lb.rated.hrs), rate: rateN(lb.rated.rate), bill: formN(lb.rated.sheet), strong: true, rule: true })}
            {Math.round(lb.adj) ? lbRow({ key: 'adj', label: 'Invoiced vs the rate sheet', how: "invoices bill the signed tickets' hours and ST / OT split, not JDE payroll's", bill: formN(lb.adj), grey: true }) : null}
            {lb.perDiem.bill ? lbRow({ key: 'pd', label: 'Per diem', how: 'at cost', bill: formN(lb.perDiem.bill) }) : null}
            {lb.other.bill ? lbRow({ key: 'other', label: 'Other labor lines', how: 'at cost', bill: formN(lb.other.bill) }) : null}
            {lb.noRate.n ? lbRow({ key: 'norate', label: `No rate yet (${lb.noRate.n} line${lb.noRate.n === 1 ? '' : 's'})`, how: 'bill $0 on Billing until mapped on Rates & Rules', hrs: hrsN(lb.noRate.hrs), bill: '0', grey: true }) : null}
            {lbRow({ key: 'jtd', label: 'Billable to date', how: lb.hrs ? '$ / hr all in, per diem included' : '', hrs: hrsN(lb.hrs), rate: rateN(lb.billPerHr), bill: formN(lb.jtdBill), strong: true, rule: true })}
            {Math.round(lb.noRateEst) ? lbRow({ key: 'noratest', label: 'No-rate lines, estimated', how: "at the job's own ST / OT billing per dollar of wage", bill: formN(lb.noRateEst) }) : null}
            {lb.toGoRows.length ? lb.toGoRows.map((r) => lbRow({
                key: `togo-${r.ct || 'noct'}`, label: `${r.ct ? CT_DESC[r.ct] || r.ct : r.label} to go`,
                how: `${formN(r.cost) || '0'} cost — ${howText(r.how) || 'at cost'}`,
                hrs: r.hrs ? `≈ ${hrsN(r.hrs)}` : '', rate: r.hrs ? rateN(r.rate) : '', bill: formN(r.bill) || '0',
            })) : lbRow({ key: 'togo', label: 'Cost to go', how: 'nothing committed or forecast', bill: '0' })}
            {lbRow({ key: 'eac', label: 'Labor Billable EAC', bill: formN(lb.billEac), strong: true, rule: true })}
            <View style={{ marginTop: 12 }}>
                <Text style={{ fontSize: 12, color: '#64748b' }}>
                    {`Cost side: labor cost to date ${formN(lb.jtdCost) || '0'}${lb.hrs ? ` ÷ ${hrsN(lb.hrs)} hours = ${rateN(lb.costPerHr)} / hr` : ''} — burden, payroll taxes and truck / rig pay included (${formN(lb.neverBilled) || '0'} of it never bills)${lb.nb.cost ? `, ${formN(lb.nb.cost)} held not billable` : ''}. Billable ${rateN(lb.billPerHr)} / hr ÷ cost ${rateN(lb.costPerHr)} / hr is the ${lb.costPerHr ? ratioText(lb.billPerHr / lb.costPerHr) : '–'} the class bills at to date.`}
                    {lb.toGoHrs ? ` About ${hrsN(lb.toGoHrs)} labor hours to go${lb.toGoOtHrs ? `, ${hrsN(lb.toGoOtHrs)} of them past 40 a week — the forecast prices those at straight wage, the rate sheet bills them at OT` : ''}; burden to go never bills.` : ''}
                </Text>
            </View>
            {lb.byClass.length > 0 && (
                <View style={{ marginTop: 12 }}>
                    <Pressable onPress={() => setOpen((p) => ({ ...p, laborCard: !p.laborCard }))} style={{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer', alignSelf: 'flex-start' }}>
                        <Icon3 name={open.laborCard ? 'MdKeyboardArrowDown' : 'MdArrowForwardIos'} size={open.laborCard ? 16 : 11} color="#2563eb" />
                        <Text style={{ fontSize: 12, color: '#2563eb', marginLeft: 4 }}>{`By rate sheet classification (${lb.byClass.length})`}</Text>
                    </Pressable>
                    {open.laborCard && (
                        <View style={{ marginTop: 6 }}>
                            {lbClassRow(null, true)}
                            {lb.byClass.map((c) => lbClassRow(c))}
                        </View>
                    )}
                </View>
            )}
        </View>
    ) : null;

    return (
        <View style={{ flexDirection: 'row' }}>
            <View style={{ marginLeft: 30 }}>
                <View style={{ marginRight: 'auto' }}>
                    <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, paddingLeft: 0, marginTop: 30, position: 'relative', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                        <View style={{ flexDirection: 'row', borderRadius: 5, position: 'absolute', top: 20, left: 30 }}>
                            <View style={{ backgroundColor: '#e0e0e0', borderRadius: 6, padding: 6, paddingHorizontal: 16, alignItems: 'center' }}>
                                <Text style={{ color: '#192440', fontWeight: 'bold', fontSize: 10 }}>LEMSO</Text>
                            </View>
                            <View style={{ padding: 6, paddingHorizontal: 10, alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ color: '#64748b', fontSize: 10 }}>Cost Plus</Text>
                            </View>
                        </View>
                        <View style={ROW}>
                            <Text style={HEAD_LEAD} />
                            <Text style={HEAD}>K2 Budget</Text>
                            <Text style={HEAD}>Bid Budget</Text>
                            <Text style={HEAD}>Approved CO's</Text>
                            <Text style={HEAD}>Current Budget</Text>
                            <View style={GAP} />
                            <Pressable onPress={() => setShowSent((v) => !v)} aria-label={SENT_TOGGLE} title={SENT_TOGGLE} aria-expanded={showSent}
                                style={{ ...HEAD, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
                                <Text style={{ fontSize: 10, fontWeight: 500, color: '#161616', textAlign: 'right' }}>JTD Actual Cost</Text>
                                <Icon3 name={showSent ? 'MdChevronLeft' : 'MdChevronRight'} size={14} color="#6b7280" />
                            </Pressable>
                            {showSent && <Text style={HEAD} title={SENT_NOTE}>JTD Cost Invoiced</Text>}
                            {showSent && <Text style={HEAD} title={UNSENT_NOTE}>JTD Cost Not Invoiced</Text>}
                            {showSent && <Text style={HEAD} title={NB_COL_NOTE}>JTD Cost Non-Billable</Text>}
                            <Text style={HEAD}>Committed Costs</Text>
                            <View style={GAP} />
                            <Text style={HEAD}>Estimate To Complete (ETC)</Text>
                            <Text style={HEAD}>Estimate At Completion (EAC)</Text>
                            <View style={GAP} />
                            <Text style={HEAD}>Delta from Budget EAC</Text>
                            <Text style={HEAD}>Delta from Bid Budget</Text>
                            <Text style={HEAD}>Delta from Prev WIP</Text>
                            <View style={GAP} />
                            <Text style={HEAD}>Billable Budget</Text>
                            <Text style={HEAD}>Billable EAC</Text>
                            <Text style={HEAD}>Delta from Billable Budget</Text>
                        </View>

                        {/* Revenue's to-date cells: JTD Cost Invoiced is what has been
                            billed, Not Invoiced the rest priced, Non-Billable bills nothing;
                            JTD + Committed + ETC is the EAC. */}
                        {gridRow({
                            key: 'revenue', label: 'Revenue (Billable)', grey: true,
                            k2: '-', bid: formN(revenue.bid), co: formN(revenue.co), current: formN(revenue.current), billBudget: formN(revenue.current),
                            jtd: formN(revenue.jtd), jtdSent: sentN(revenue.sentBill), sentNote: BILLED_NOTE, jtdUnsent: sentN(revenue.unsentBill), unsentNote: TO_BILL_NOTE, jtdNb: '',
                            committed: formN(revenue.committed), etc: formN(revenue.etc), eac: formN(revenue.eac), billEac: formN(revenue.eac),
                            deltaEac: formN(revenue.deltaEac), deltaBid: formN(revenue.deltaBid), deltaBill: formN(revenue.deltaEac), deltaWip: '',
                            bill: model.ready && revenue.contract != null
                                ? `Revenue EAC = ${revenue.tiedTo === 'budget' ? `the current revenue budget ${n0(revenue.contract)} (Revenue Budget + approved COs)` : `the contract ${n0(revenue.contract)} (Day Zero revenue + approved CO revenue)`}; ETC = what's left of it after invoiced ${n0(revenue.sentBill)}, not invoiced ${n0(revenue.unsentBill)} and committed ${n0(revenue.committed)}. As priced it would be ${n0(revenue.pricedEac)} — see the Billable EAC card`
                                : model.ready
                                ? `Billable EAC = invoiced ${n0(revenue.sentBill)}${revenue.noLineSent ? ` (${n0(revenue.noLineSent)} of it billed with no JDE line)` : ''} + not invoiced ${n0(revenue.unsentBill)} + committed ${n0(revenue.committed)} + ETC ${n0(revenue.etc)} — see the Billable EAC card`
                                : undefined,
                        })}
                        <View style={{ width: '100%', height: 25 }} />

                        {model.classes.map(classRows)}

                        {gridRow({
                            key: 'contingency', label: 'Contingency',
                            k2: '', bid: formN(model.contingency.costBudget), co: '', current: formN(model.contingency.curCostBudget), billBudget: formN(model.contingency.curBillBudget),
                            jtd: '', committed: '', etc: formN(model.contingency.etc), eac: formN(model.contingency.costEac), billEac: formN(model.contingency.billEac),
                            deltaEac: formN(model.contingency.costDeltaEac), deltaBid: formN(model.contingency.costDeltaBid), deltaBill: formN(model.contingency.billDeltaEac), deltaWip: '', bill: billNote(model.contingency),
                        })}
                        {nonBillableRows()}
                        {gridRow({
                            key: 'total', label: 'Total Costs', grey: true,
                            k2: '', bid: formN(total.costBudget), co: formN(total.coCost), current: formN(total.curCostBudget), billBudget: formN(total.curBillBudget),
                            jtd: formN(total.jtdCost), jtdSent: sentN(total.sentCost), jtdUnsent: sentN(total.unsentCost), jtdNb: sentN(total.jtdNb), committed: formN(total.committed), etc: formN(total.etc), eac: formN(total.costEac), billEac: formN(total.billEac),
                            deltaEac: formN(total.costDeltaEac), deltaBid: formN(total.costDeltaBid), deltaBill: formN(total.billDeltaEac), deltaWip: '',
                            bill: billNote({ ...total, toGoHow: [] }),
                        })}
                        <div style={{ width: '100%', height: 30, borderBottomWidth: 3, borderColor: '#F9F8FB' }} />
                        {/* Job Profit is revenue less cost in every column, so the to-date
                            cells foot to JTD and JTD + Committed + ETC to the EAC. */}
                        {gridRow({
                            key: 'jp', label: 'Job Profit',
                            k2: '', bid: formN(profit.bid), co: formN(profit.co), current: formN(profit.current), billBudget: '',
                            jtd: formN(profit.jtd), jtdSent: sentN(profit.sent), jtdUnsent: sentN(profit.unsent), jtdNb: sentN(profit.nb),
                            committed: formN(profit.committed), etc: formN(profit.etc), eac: formN(profit.eac), billEac: '',
                            deltaEac: formN(profit.deltaEac), deltaBid: formN(profit.deltaBid), deltaWip: '',
                        })}
                        {gridRow({
                            key: 'jpp', label: 'JP %',
                            k2: '', bid: pct2(profit.bidPct), co: '', current: pct2(profit.currentPct), billBudget: '',
                            jtd: pct2(profit.jtdPct), jtdSent: pct2(profit.sentPct), jtdUnsent: pct2(profit.unsentPct), jtdNb: '',
                            committed: pct2(profit.committedPct), etc: pct2(profit.etcPct), eac: pct2(profit.eacPct), billEac: '',
                            deltaEac: pct2(profit.deltaEacPct), deltaBid: pct2(profit.deltaBidPct), deltaWip: '',
                        })}
                        <div style={{ width: '100%', height: 30, borderBottomWidth: 3, borderColor: '#F9F8FB' }} />
                        {gridRow({
                            key: 'oh', label: 'OH Allocation',
                            k2: '', bid: formN(oh.bid), co: '', current: formN(oh.current), billBudget: '',
                            jtd: formN(oh.jtd), committed: '', etc: formN(oh.etc), eac: formN(oh.eac), billEac: '',
                            deltaEac: formN(oh.deltaEac), deltaBid: formN(oh.deltaBid), deltaWip: '',
                        })}
                        {gridRow({
                            key: 'gm', label: 'Gross Margin',
                            k2: '', bid: formN(gm.bid), co: '', current: formN(gm.current), billBudget: '',
                            jtd: '', committed: '', etc: '', eac: formN(gm.eac), billEac: '',
                            deltaEac: formN(gm.deltaEac), deltaBid: formN(gm.deltaBid), deltaWip: '',
                        })}
                        {gridRow({
                            key: 'gmp', label: 'GM %',
                            k2: '', bid: pct2(gm.bidPct), co: '', current: pct2(gm.currentPct), billBudget: '',
                            jtd: '', committed: '', etc: '', eac: pct2(gm.eacPct), billEac: '',
                            deltaEac: pct2(gm.deltaEacPct), deltaBid: pct2(gm.deltaBidPct), deltaWip: '',
                        })}
                        {status.length > 0 && (
                            <View style={{ marginTop: 10, marginLeft: 30 }}>
                                {status.map((t, i) => <Text key={i} style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>{t}</Text>)}
                            </View>
                        )}
                    </View>

                    <View style={{ flexDirection: 'row', marginTop: 10 }}>
                        <View style={[card, { marginLeft: 0 }]}>
                            <View style={{ flexDirection: 'row', marginBottom: 10, justifyContent: 'flex-end' }}>
                                <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: '500', marginRight: 40 }}>Prev WIP</Text>
                                <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: '500' }}>% Complete</Text>
                            </View>
                            {cardRow('Costs', pct.cost == null ? '' : `${(pct.cost * 100).toFixed()}%`)}
                            {cardRow('Labor', pct.labor == null ? '' : `${(pct.labor * 100).toFixed()}%`)}
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={{ flex: 1, textAlign: 'left', fontSize: 14 }}>Physical</Text>
                                <Text style={{ flex: 1, textAlign: 'right', fontSize: 14, paddingRight: 40 }} />
                                {cardInput('physicalComplete', 87)}
                            </View>
                            {cardRow('Billings', pct.bill == null ? '-' : `${(pct.bill * 100).toFixed()}%`)}
                        </View>
                        <View style={card}>
                            <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Billings</Text>
                            {cardRow('Earned Rev Thru Prev Period', formN(billing.prevPeriod))}
                            {cardRow('Estimate Earned Rev', formN(billing.jtdBill))}
                            {cardRow('Billings', formN(billing.jdeBilled) || '-')}
                            {cardRow('Over/(Under)', formN(billing.overUnder))}
                            {cardRow('Left to Bill', formN(billing.leftToBill))}
                            {SHOW_NTE && cardRow('NTE Amount', null, cardInput('nteAmount', 150))}
                            {SHOW_NTE && cardRow('NTE Headroom', nte.headroom == null ? '' : `${formN(nte.headroom)}${nte.headroomPct != null ? `  (${(nte.headroomPct * 100).toFixed(1)}%)` : ''}`)}
                            {SHOW_NTE && cardRow(`Run Rate (${nte.runRateWeeks} wk)`, nte.runRate == null ? '' : `${formN(nte.runRate)} / wk`)}
                            {SHOW_NTE && cardRow('NTE Reached at Run Rate', nte.reachedOn ? `${moment(nte.reachedOn).format('M/D/YY')}${projectComplete && moment(nte.reachedOn).isBefore(moment(projectComplete)) ? ' — before completion' : ''}` : '')}
                        </View>
                    </View>
                    {billableCard}
                    {laborCard}
                    <View style={{ marginTop: 10, backgroundColor: 'white', borderRadius: 5, padding: 20, height: 400, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                        <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Notes</Text>
                        <TextInput
                            value={fields.notes}
                            editable={!lockedVersion}
                            onChangeText={(n) => saveField('notes', n)}
                            placeholder="Major Cost/Gross Profit Variance Explanations this month"
                            multiline={true}
                            style={{ flex: 1, padding: 20, fontSize: 14, backgroundColor: '#F9F8FB', borderRadius: 5 }} />
                    </View>
                </View>
            </View>
        </View>
    );
}

export default CostPlusForecast;
